import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const AdminAuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null); // null = checking, object = authed, false = not authed
  // Only every logged-out storefront visitor was silently firing GET /api/auth/me
  // (and logging a 401 to the console) on EVERY public page load, since this
  // provider wraps the whole app. No public component reads `admin` state —
  // only skip the check entirely outside /admin/*.
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdminRoute) return;
    axios
      .get(`${API}/api/auth/me`, { withCredentials: true })
      .then((res) => setAdmin(res.data))
      .catch(() => setAdmin(false));
  }, [isAdminRoute]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      setAdmin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setAdmin(false);
    }
  };

  const updateProfile = async (currentPassword, newEmail, newPassword) => {
    try {
      const { data } = await axios.put(
        `${API}/api/auth/profile`,
        { current_password: currentPassword, new_email: newEmail || null, new_password: newPassword || null },
        { withCredentials: true }
      );
      setAdmin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return <AdminAuthContext.Provider value={{ admin, login, logout, updateProfile }}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => useContext(AdminAuthContext);
